import React, { useMemo } from 'react';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { usePdmAssignmentStyles } from '../../styles/PdmAssignmentPage.styles';
import { PDM_REGIONS, NO_REGION_LABEL } from '../../constants/pdmRegions';
import type { PartnerAssignmentRow } from '../../models/pdmAssignment';

/**
 * Coverage = the PDM role only. A partner is "unassigned" when its PDM column is
 * empty; the PDM manager and other-roles columns do not affect coverage (FR-4).
 */
export function countUnassigned(rows: PartnerAssignmentRow[]): number {
  return rows.reduce((n, r) => (r.pdm.length === 0 ? n + 1 : n), 0);
}

function regionalBreakdown(rows: PartnerAssignmentRow[]): string {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (r.pdm.length === 0) {
      const region = r.region || NO_REGION_LABEL;
      counts.set(region, (counts.get(region) ?? 0) + 1);
    }
  }
  const ordered = [
    ...PDM_REGIONS.filter((reg) => counts.has(reg)),
    ...[...counts.keys()].filter((reg) => !(PDM_REGIONS as readonly string[]).includes(reg)),
  ];
  return ordered.map((reg) => `${reg} ${counts.get(reg)}`).join(' · ');
}

interface CoverageBannerProps {
  rows: PartnerAssignmentRow[];
  dismissed: boolean;
  onDismiss: () => void;
  onShowUnassigned: () => void;
}

/**
 * Full-width banner shown only when at least one partner is unassigned (FR-6, FR-7).
 * Dismiss persists for the page lifetime via parent component state — never
 * sessionStorage/localStorage (FR-8).
 */
export const CoverageBanner: React.FC<CoverageBannerProps> = ({
  rows,
  dismissed,
  onDismiss,
  onShowUnassigned,
}) => {
  const styles = usePdmAssignmentStyles();
  const unassigned = useMemo(() => countUnassigned(rows), [rows]);
  const breakdown = useMemo(() => regionalBreakdown(rows), [rows]);

  if (unassigned === 0 || dismissed) return null;

  return (
    <MessageBar intent="warning" layout="singleline" className={styles.banner} role="status">
      <MessageBarBody className={styles.bannerBody}>
        <MessageBarTitle>
          {unassigned} {unassigned === 1 ? 'partner does' : 'partners do'} not have PDM assigned.
        </MessageBarTitle>
        <span className={styles.bannerBreakdown}>{breakdown}</span>
      </MessageBarBody>
      <MessageBarActions
        containerAction={
          <Button
            appearance="transparent"
            size="small"
            icon={<DismissRegular />}
            aria-label="Dismiss coverage banner"
            onClick={onDismiss}
          />
        }
      >
        <Button size="medium" onClick={onShowUnassigned}>
          Show unassigned
        </Button>
      </MessageBarActions>
    </MessageBar>
  );
};
